// Particles. Nothing here writes depth or sorts — everything renders on the one
// blended layer added in `scene.ts`, drawn after the actors.
//
// A particle is a position, a velocity, a lifetime and a list of atlas frames it
// flips through. That is enough for every effect the game asks for, so the
// presets below are the whole vocabulary: emit one by name and forget about it.
import type { Scene } from "./scene.ts";

export type FxKind =
  | "dirt"
  | "splash"
  | "leaves"
  | "sparkle"
  | "ripple"
  | "zzz"
  | "spray"
  | "butterfly"
  | "puddle"
  | "shadow"
  | "lightning"
  | "glow";

interface Preset {
  frames: string[];
  ttl: number;
  /** tiles per second, randomised per particle */
  speed: number;
  spread: number;
  /** upward push at birth, tiles per second */
  lift: number;
  gravity: number;
  /** how many quads one emit() produces */
  count: number;
  /** fade the last fraction of life */
  fade: number;
  scale: number;
}

const PRESETS: Record<FxKind, Preset> = {
  dirt: { frames: ["fx_dirt_0", "fx_dirt_1", "fx_dirt_2"], ttl: 0.42, speed: 1.1, spread: 1, lift: 1.6, gravity: 6, count: 1, fade: 0.4, scale: 1 },
  splash: { frames: ["fx_splash_0", "fx_splash_1", "fx_splash_2"], ttl: 0.5, speed: 0.5, spread: 0.6, lift: 0.5, gravity: 2.4, count: 1, fade: 0.5, scale: 1 },
  leaves: { frames: ["fx_leaves_0", "fx_leaves_1", "fx_leaves_2"], ttl: 1.1, speed: 0.8, spread: 1.4, lift: 1.2, gravity: 1.4, count: 3, fade: 0.45, scale: 1 },
  sparkle: { frames: ["fx_sparkle_0", "fx_sparkle_1", "fx_sparkle_2", "fx_sparkle_3"], ttl: 0.6, speed: 0.7, spread: 1.2, lift: 1.4, gravity: 0.6, count: 4, fade: 0.5, scale: 1 },
  ripple: { frames: ["fx_ripple_0", "fx_ripple_1", "fx_ripple_2"], ttl: 0.9, speed: 0, spread: 0, lift: 0, gravity: 0, count: 1, fade: 0.5, scale: 1 },
  zzz: { frames: ["fx_zzz_0", "fx_zzz_1", "fx_zzz_2"], ttl: 1.6, speed: 0.15, spread: 0.3, lift: 0.5, gravity: -0.2, count: 1, fade: 0.5, scale: 1 },
  spray: { frames: ["sprinkler_spray_0", "sprinkler_spray_1", "sprinkler_spray_2"], ttl: 1.1, speed: 0, spread: 0, lift: 0, gravity: 0, count: 1, fade: 0.35, scale: 1 },
  butterfly: { frames: ["butterfly_0", "butterfly_1", "butterfly_2"], ttl: 5.5, speed: 0.45, spread: 1.2, lift: 0.2, gravity: -0.05, count: 1, fade: 0.25, scale: 1 },
  puddle: { frames: ["fx_puddle"], ttl: 12, speed: 0, spread: 0, lift: 0, gravity: 0, count: 1, fade: 0.3, scale: 1 },
  shadow: { frames: ["fx_shadow"], ttl: 0.05, speed: 0, spread: 0, lift: 0, gravity: 0, count: 1, fade: 0, scale: 1 },
  lightning: { frames: ["fx_lightning"], ttl: 0.35, speed: 0, spread: 0, lift: 0, gravity: 0, count: 1, fade: 0.7, scale: 1 },
  glow: { frames: ["fx_lamp_glow"], ttl: 0.05, speed: 0, spread: 0, lift: 0, gravity: 0, count: 1, fade: 0, scale: 1 },
};

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  t: number;
  ttl: number;
  frames: string[];
  fade: number;
  gravity: number;
  tint: readonly [number, number, number];
  alpha: number;
  /** flips through the frames once, rather than looping */
  loop: boolean;
}

export interface EmitOptions {
  tint?: readonly [number, number, number];
  alpha?: number;
  ttl?: number;
  count?: number;
  loop?: boolean;
  /** bias the burst in a direction, in tiles per second */
  vx?: number;
  vy?: number;
}

const MAX_PARTICLES = 512;
const WHITE: readonly [number, number, number] = [1, 1, 1];

export class Particles {
  private readonly live: Particle[] = [];
  private seed = 1;

  /** Deterministic jitter — the e2e run must not drift frame to frame. */
  private rnd(): number {
    this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
    return this.seed / 0x7fffffff;
  }

  get count(): number {
    return this.live.length;
  }

  clear(): void {
    this.live.length = 0;
  }

  /** Burst at a tile-space point. `y` is the centre, not the base. */
  emit(kind: FxKind, x: number, y: number, o: EmitOptions = {}): void {
    const p = PRESETS[kind];
    const n = o.count ?? p.count;
    for (let i = 0; i < n; i++) {
      if (this.live.length >= MAX_PARTICLES) return;
      const a = this.rnd() * Math.PI * 2;
      const s = p.speed * (0.5 + this.rnd());
      this.live.push({
        x: x + (this.rnd() - 0.5) * p.spread * 0.5,
        y: y + (this.rnd() - 0.5) * p.spread * 0.3,
        vx: (o.vx ?? 0) + Math.cos(a) * s,
        vy: (o.vy ?? 0) + Math.sin(a) * s * 0.5 - p.lift,
        t: 0,
        ttl: (o.ttl ?? p.ttl) * (0.85 + this.rnd() * 0.3),
        frames: p.frames,
        fade: p.fade,
        gravity: p.gravity,
        tint: o.tint ?? WHITE,
        alpha: o.alpha ?? 1,
        loop: o.loop ?? false,
      });
    }
  }

  update(dt: number): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const p = this.live[i]!;
      p.t += dt;
      if (p.t >= p.ttl) {
        this.live.splice(i, 1);
        continue;
      }
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
  }

  draw(scene: Scene): void {
    for (const p of this.live) {
      const u = p.t / p.ttl;
      const idx = p.loop
        ? Math.floor(p.t * 8) % p.frames.length
        : Math.min(p.frames.length - 1, Math.floor(u * p.frames.length));
      const a = u > 1 - p.fade && p.fade > 0 ? p.alpha * ((1 - u) / p.fade) : p.alpha;
      scene.fxSprite(p.frames[idx]!, p.x, p.y, a, p.tint);
    }
  }
}
